import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { last } from 'lodash';
import gql from 'graphql-tag';

import { Alert, EllipsedLine } from '../../ui';

import agentLogRowFragment from '../../fragments/agentLogRowFragment';
import conversationFragment from '../../fragments/conversationFragment';

import withData from '../../decorators/withData';
import withRecentView from '../../decorators/withRecentView';
import withRouter from '../../decorators/withRouter';

import { RightPanelProvider } from '../../contexts/RightPanelContext';

import { track } from '../../services/Analytics';

import useAgentChatStream from '../../hooks/useAgentChatStream';
import useLocation from '../../hooks/useLocation';

import AccountLink from '../links/AccountLink';
import Actions from '../Actions';
import AgentChat from '../AgentChat';
import AgentLogRows from '../AgentLogRows';
import Conversation from '../Conversation';
import { DataContext } from '../Data';
import FixedLayout from '../FixedLayout';
import InfoPanel from '../InfoPanel';
import MainDisplayObjectPic from '../MainDisplayObjectPic';
import Path from '../Path';
import StarControl from '../StarControl';

import composeWithNonDialogComponents from '../../utils/composeWithNonDialogComponents';
import getMainDisplayObjectName from '../../utils/getMainDisplayObjectName';
import getSegmentMainDisplayObjectVariables from '../../utils/getSegmentMainDisplayObjectVariables';

const SEGMENT_PAGE_CTXT = 'ConversationPage';

const QUERY_CONVERSATION = gql`
  query conversation($id: DokkioID!) {
    conversation(id: $id) {
      ...conversationFragment
      agentLogRows {
        ...agentLogRowFragment
      }
    }
  }
  ${agentLogRowFragment}
  ${conversationFragment}
`;

const MAX_TOKENS_BEFORE_DISABLE_INPUT_COUNT = 200000;

export const ConversationView = () => {
  const { push } = useHistory();
  const { query: { active: activeString } } = useLocation();
  const {
    conversation,
    conversation: {
      accountID,
      id,
      remoteConversationID,
      conversationUserPrompts,
      agentLogRows,
    },
    refetchConversation,
    integrations,
  } = useContext(DataContext);

  const hasTrackedOverConvLimitRef = useRef(false);
  const lastTotalTokensRef = useRef(null);
  const [isOverMaxTokens, setIsOverMaxTokens] = useState(false);

  const isActive = activeString === 'true';
  const lastConversationUserPrompt = last(conversationUserPrompts) || {};
  const isStillLoadingText = integrations.some(({ textLoadingCount }) => textLoadingCount > 0);

  useEffect(() => {
    hasTrackedOverConvLimitRef.current = false;
    lastTotalTokensRef.current = null;
    track('Conversation Viewed', getSegmentMainDisplayObjectVariables({
      mainDisplayObject: conversation,
    }));
  }, [id]);

  const trackOverConvLimit = () => {
    if (hasTrackedOverConvLimitRef.current) {
      return;
    }
    hasTrackedOverConvLimitRef.current = true;
    // Tool results also arrive as user-role rows, so only count text
    // prompts; the new turn isn't in agentLogRows yet
    const promptCount = agentLogRows.filter(({ role, parts }) => (
      role === 'user' && parts.some(part => part.type === 'text')
    )).length + 1;
    track('Chat OverConvLimit', {
      PromptCount: promptCount,
      TokenCount: lastTotalTokensRef.current,
    });
  };

  const { currentAgentLogRows, isSubmitting, sendPrompt } = useAgentChatStream({
    path: '/v2/mcp.chat',
    onEvent: ({ error, usage }) => {
      if (error) {
        if (!error.message.includes('Input is too long for requested model')) {
          throw error.message;
        }
        trackOverConvLimit();
        setIsOverMaxTokens(true);
      } else if (usage) {
        lastTotalTokensRef.current = usage.totalTokens;
        if (usage.totalTokens >= MAX_TOKENS_BEFORE_DISABLE_INPUT_COUNT) {
          trackOverConvLimit();
          setIsOverMaxTokens(true);
        }
      }
    },
    onStreamEnd: refetchConversation,
  });

  const onContinueConversation = async ({ prompt }, { reset = () => null } = {}) => {
    track('Chat UserPrompt', {
      Prompt: prompt,
    });
    reset();
    await sendPrompt({ prompt, remoteConversationID });
  };

  useEffect(() => {
    if (!agentLogRows.length) {
      onContinueConversation({ prompt: lastConversationUserPrompt.prompt });
    }
  }, [conversationUserPrompts.length]);

  const renderHeaderActions = () => (
    <Actions
      actionContext="pageHeader"
      buttonActions={[{
        action: 'open',
      }]}
      dropdownActions={[{
        action: 'createTask',
      }, {
        action: 'star',
      }, {
        action: 'rename',
      }, {
        action: 'setDokkioTags',
      }, {
        action: 'delete',
        onSuccess: () => push(`/a/${accountID}/conversations`),
      }]}
      onSuccess={refetchConversation}
      segmentPageCtxt={SEGMENT_PAGE_CTXT}
      targets={[conversation]}
    />
  );

  return (
    <RightPanelProvider>
      <FixedLayout
        renderPic={({ width, height, iconPx }) => (
          <MainDisplayObjectPic
            height={height}
            width={width}
            iconPx={iconPx}
            mainDisplayObject={conversation}
          />
        )}
        renderActions={renderHeaderActions}
        renderTitle={() => (
          <React.Fragment>
            <EllipsedLine>
              {getMainDisplayObjectName({ mainDisplayObject: conversation })}
            </EllipsedLine>
            <StarControl object={conversation} segmentPageCtxt={SEGMENT_PAGE_CTXT} />
          </React.Fragment>
        )}
        renderDescription={() => (
          <Path
            mainDisplayObject={conversation}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
          />
        )}
        renderPanel={() => (
          <InfoPanel
            mainDisplayObject={conversation}
            onObjectsChanged={refetchConversation}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
          />
        )}
      >
        <AgentChat
          key={id}
          placeholder="Reply to Dokkio"
          currentAgentLogRows={currentAgentLogRows}
          isSubmitting={isSubmitting}
          isAutoScrollEnabled={isActive}
          isInputVisible={isActive && !isOverMaxTokens}
          onSendPrompt={onContinueConversation}
          formContent={isOverMaxTokens && !isSubmitting && (
            <Alert alertStyle="info">
              The conversation is too long to continue,
              {' '}
              <AccountLink to="/conversations">please start a new one</AccountLink>
            </Alert>
          )}
        >
          {!conversation.isTextLoadingComplete && (
            <Alert alertStyle="info">
              This response may be incomplete. Your content was still
              being scanned and indexed when it was generated.
              {!isStillLoadingText && (
                <React.Fragment>
                  {' '}
                  You can
                  {' '}
                  <AccountLink to="/conversations">try your question again</AccountLink>
                </React.Fragment>
              )}
            </Alert>
          )}
          <Conversation
            allowRating={isActive}
            agentLogRows={agentLogRows}
            conversation={conversation}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
          />
          <AgentLogRows
            agentLogRows={currentAgentLogRows}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
            isSubmitting={isSubmitting}
          />
        </AgentChat>
      </FixedLayout>
    </RightPanelProvider>
  );
};

export default composeWithNonDialogComponents(
  withRouter,
  withRecentView,
  withData(({ match: { params: { conversationID } } }) => ({
    queries: [{
      query: QUERY_CONVERSATION,
      variables: { id: conversationID },
    }],
    showLoader: 'initial-load-only',
  })),
)(ConversationView);
